import { useCallback, useEffect, useMemo, useState } from 'react';
import Icon from '../../../../components/Icon/Icon';
import { assistantService } from '../../../../services/assistants/Assistants';
import { Assistant } from '../../../../services/assistants/Assistants.types';
import AssistantDialog from '../AssistantDialog/AssistantDialog';
import { AssistantSkeleton } from '../AssistantSkeleton/AssistantSkeleton';
import AssistantListCard from '../Assistantlistcard/Assistantlistcard';
import './Assistantlist.css';

type AssistantCategory = 'boss' | 'administration' | 'programming';
type FilterId = 'all' | AssistantCategory;

const categories: { id: AssistantCategory; label: string }[] = [
  { id: 'boss', label: 'Jefes' },
  { id: 'administration', label: 'Administración' },
  { id: 'programming', label: 'Programación' },
];

const filters: { id: FilterId; label: string }[] = [{ id: 'all', label: 'Todos' }, ...categories];

export const normalizeCharge = (charge: string) => charge
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .toLocaleLowerCase('es');

export const classifyAssistant = (assistant: Assistant): AssistantCategory => {
  const charge = normalizeCharge(assistant.charge);
  if (/\bjef[ae]\b|\bcoordinador[ae]?\b|\blider\b/.test(charge)) return 'boss';
  if (/program|desarroll|software/.test(charge)) return 'programming';
  if (/admin|comunica|logistica|espacio/.test(charge)) return 'administration';
  return 'administration';
};

const alphabetize = (items: Assistant[]) => [...items].sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));

const AssistantList = () => {
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [filter, setFilter] = useState<FilterId>('all');
  const [selectedAssistant, setSelectedAssistant] = useState<Assistant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadAssistants = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await assistantService();
      const items = response.data?.assistantCollection?.items;
      if (!Array.isArray(items)) throw new Error('Respuesta inválida de Contentful');
      setAssistants(items);
    } catch (_) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAssistants(); }, [loadAssistants]);

  const groupedAssistants = useMemo(() => categories.map((category) => ({
    ...category,
    items: alphabetize(assistants.filter((assistant) => classifyAssistant(assistant) === category.id)),
  })).filter((category) => filter === 'all' ? category.items.length > 0 : category.id === filter), [assistants, filter]);

  const visibleCount = groupedAssistants.reduce((total, group) => total + group.items.length, 0);
  const closeAssistantDialog = useCallback(() => setSelectedAssistant(null), []);

  return (
    <section className="lis-container assistants-content" aria-labelledby="assistants-list-title" aria-busy={loading}>
      <div className="assistants-list-heading">
        <div><p className="lis-eyebrow">Equipo actual</p><h2 id="assistants-list-title">Personas que cuidan nuestros espacios</h2></div>
        {!loading && !error && <p aria-live="polite">{visibleCount} {visibleCount === 1 ? 'integrante' : 'integrantes'}</p>}
      </div>

      {!error && (
        <div className="assistant-filters" role="group" aria-label="Filtrar auxiliares por área">
          {filters.map(({ id, label }) => <button key={id} type="button" className={`assistant-filter${filter === id ? ' assistant-filter--active' : ''}`} onClick={() => setFilter(id)} aria-pressed={filter === id} disabled={loading}>{label}</button>)}
        </div>
      )}

      {error ? (
        <div className="assistants-error" role="alert">
          <span className="state-icon state-icon--error"><Icon name="alert" /></span>
          <h2>No pudimos cargar los auxiliares</h2>
          <p>La información proviene de Contentful y no está disponible en este momento.</p>
          <button type="button" className="lis-button lis-button--primary" onClick={loadAssistants}><Icon name="refresh" className="lis-inline-icon" /> Intentar de nuevo</button>
        </div>
      ) : loading ? (
        <div className="assistants-grid assistants-grid--loading" aria-label="Cargando auxiliares">
          {Array.from({ length: 6 }, (_, index) => <AssistantSkeleton key={index} />)}
        </div>
      ) : (
        <div className="assistant-groups">
          {groupedAssistants.map((group) => (
            <section className="assistant-group" key={group.id} aria-labelledby={`assistant-group-${group.id}`}>
              <div className="assistant-group-heading"><h3 id={`assistant-group-${group.id}`}>{group.label}</h3><span>{group.items.length}</span></div>
              <ul className="assistants-grid">
                {group.items.map((assistant) => <li key={`${filter}-${assistant.name}`}><AssistantListCard assistant={assistant} onSelect={setSelectedAssistant} /></li>)}
              </ul>
            </section>
          ))}
        </div>
      )}

      {selectedAssistant && <AssistantDialog assistant={selectedAssistant} onClose={closeAssistantDialog} />}
    </section>
  );
};

export default AssistantList;
